import React, { useState } from 'react';
import { Drawer, Input, Select, Switch, Button } from 'antd';
import { STATE_OPTIONS, CHANNEL_OPTIONS, FREQUENCY_OPTIONS } from './segment/operators';
import { buildSummary } from './segment/segmentHumanize';
import { buildProductById } from './segment/segmentRead';
import SegmentSummary from './segment/SegmentSummary';
import SegmentEditorModal from './segment/SegmentEditorModal';
import AdvancedOnly from './AdvancedOnly';
import './timeline.css';

// Controlled presentational drawer. All state/behavior comes from `editor`
// (the useJourneyEditor return). The page owns open/close + save.
export default function JourneyEditorDrawer({ open, editor, onRequestClose, onSave, onDelete = () => {}, attributeById }) {
  const [segOpen, setSegOpen] = useState(false);
  if (!editor) return null;
  const { values: form, set, isCreate, canSave, saving, segmentDraft, setSegmentDraft, labelUuids, setLabelUuids, labelOptions, productOptions } = editor;
  const setInput = (k) => (e) => set(k)(e.target.value);
  // Resolve product names for the summary from the already-loaded productOptions, using the
  // same shared helper the journey card uses — so card and drawer name products identically.
  const summaryModel = buildSummary(segmentDraft.groups, segmentDraft.conditionOperator, { attributeById, productById: buildProductById(productOptions) });

  return (
    <Drawer
      title={isCreate ? 'Add journey' : 'Configure journey'}
      placement="right"
      width={460}
      mask={false}
      visible={open}
      onClose={onRequestClose}
      footer={
        <div className="tp-footer">
          <div className="tp-footer-left">
            {/* Delete lives here (not on the panel) and is advanced-gated. Only an existing
                journey can be deleted — hidden while creating one. */}
            {!isCreate && (
              <AdvancedOnly>
                <Button danger type="text" aria-label="delete journey" onClick={onDelete}>Delete</Button>
              </AdvancedOnly>
            )}
          </div>
          <div className="tp-footer-right">
            <Button onClick={onRequestClose}>Cancel</Button>
            <AdvancedOnly><Button type="primary" loading={saving} disabled={!canSave} onClick={onSave}>
              {isCreate ? 'Create journey' : 'Save changes'}
            </Button></AdvancedOnly>
          </div>
        </div>
      }
    >
      <div className="tp-grp">
        <div className="tp-grp-h">Content</div>
        <label className="tp-lbl" htmlFor="j-name">Name</label>
        <AdvancedOnly><Input id="j-name" aria-label="Journey name" value={form.name} onChange={setInput('name')} /></AdvancedOnly>
        <label className="tp-lbl">State</label>
        <AdvancedOnly><select aria-label="Journey state" value={form.state} onChange={(e) => set('state')(e.target.value)}>
          {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select></AdvancedOnly>
      </div>

      <div className="tp-grp">
        <div className="tp-grp-h">Delivery &amp; order</div>
        <label className="tp-lbl">Channels</label>
        <AdvancedOnly><Select mode="multiple" aria-label="Channels" style={{ width: '100%' }} value={form.channels} onChange={set('channels')} options={CHANNEL_OPTIONS} /></AdvancedOnly>
        <div className="tp-lbl tp-toggle"><span>Restartable</span><AdvancedOnly><Switch aria-label="Restartable" checked={form.isRestartable} onChange={set('isRestartable')} /></AdvancedOnly></div>
        <div className="tp-lbl tp-toggle"><span>Recurring</span><AdvancedOnly><Switch aria-label="Recurring" checked={form.isRecurring} onChange={set('isRecurring')} /></AdvancedOnly></div>
        {form.isRecurring && (
          <>
            <label className="tp-lbl">Frequency</label>
            <AdvancedOnly><select aria-label="Recurrence frequency" value={form.frequency} onChange={(e) => set('frequency')(e.target.value)}>
              <option value="">Select…</option>
              {FREQUENCY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select></AdvancedOnly>
            <label className="tp-lbl">Start date</label>
            <AdvancedOnly><Input type="date" aria-label="Recurrence start date" value={form.startDate} onChange={setInput('startDate')} /></AdvancedOnly>
            <label className="tp-lbl">Time (UTC)</label>
            <AdvancedOnly><Input type="time" aria-label="Recurrence time" value={form.time} onChange={setInput('time')} /></AdvancedOnly>
          </>
        )}
      </div>

      <div className="tp-grp">
        <div className="tp-grp-h">Segmentation</div>
        <div className="seg-preview-card"><SegmentSummary model={summaryModel} /></div>
        <AdvancedOnly><button type="button" className="tp-add seg-edit-btn" onClick={() => setSegOpen(true)}>Edit segmentation</button></AdvancedOnly>
      </div>

      <div className="tp-grp">
        <div className="tp-grp-h">Labels</div>
        <AdvancedOnly><Select mode="multiple" aria-label="Journey labels" style={{ width: '100%' }} value={labelUuids} onChange={setLabelUuids} optionFilterProp="label" options={labelOptions} /></AdvancedOnly>
      </div>

      <SegmentEditorModal
        open={segOpen}
        draft={segmentDraft}
        productOptions={productOptions}
        attributeById={attributeById}
        onCancel={() => setSegOpen(false)}
        onDone={(next) => { setSegmentDraft(next); setSegOpen(false); }}
      />
    </Drawer>
  );
}
